import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { coOwnershipInvitationService } from '../services/coOwnershipInvitationService';
import type { SendInvitationRequest } from '../types/coOwnershipInvitation';

export const INVITATION_KEYS = {
  byApartment: (apartmentId: string) =>
    ['co-ownership-invitations', 'apartment', apartmentId] as const,
  byToken: (token: string) =>
    ['co-ownership-invitations', 'token', token] as const,
  received: () =>
    ['co-ownership-invitations', 'received'] as const,
};

export const useApartmentInvitations = (apartmentId: string) => {
  return useQuery({
    queryKey: INVITATION_KEYS.byApartment(apartmentId),
    queryFn: () => coOwnershipInvitationService.listByApartment(apartmentId),
    enabled: !!apartmentId,
  });
};

export const useInvitationByToken = (token: string) => {
  return useQuery({
    queryKey: INVITATION_KEYS.byToken(token),
    queryFn: () => coOwnershipInvitationService.getByToken(token),
    enabled: !!token,
    retry: false,
  });
};

export const useSendInvitation = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ apartmentId, request }: { apartmentId: string; request: SendInvitationRequest }) =>
      coOwnershipInvitationService.sendInvitation(apartmentId, request),
    onSuccess: (_data, { apartmentId }) => {
      queryClient.invalidateQueries({ queryKey: INVITATION_KEYS.byApartment(apartmentId) });
    },
  });
};

export const useRevokeInvitation = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ apartmentId, invitationId }: { apartmentId: string; invitationId: string }) =>
      coOwnershipInvitationService.revokeInvitation(apartmentId, invitationId),
    onSuccess: (_data, { apartmentId }) => {
      queryClient.invalidateQueries({ queryKey: INVITATION_KEYS.byApartment(apartmentId) });
    },
  });
};

export const useResendInvitation = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ apartmentId, invitationId }: { apartmentId: string; invitationId: string }) =>
      coOwnershipInvitationService.resendInvitation(apartmentId, invitationId),
    onSuccess: (_data, { apartmentId }) => {
      queryClient.invalidateQueries({ queryKey: INVITATION_KEYS.byApartment(apartmentId) });
    },
  });
};

export const useReceivedInvitations = () => {
  return useQuery({
    queryKey: INVITATION_KEYS.received(),
    queryFn: coOwnershipInvitationService.listReceived,
  });
};

export const useReceivedInvitationsPendingCount = () => {
  const { data } = useReceivedInvitations();
  return data?.filter(inv => inv.status === 'PENDING').length ?? 0;
};

export const useAcceptInvitation = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ apartmentId, token }: { apartmentId: string; token: string }) =>
      coOwnershipInvitationService.acceptInvitation(apartmentId, token),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: INVITATION_KEYS.received() });
      queryClient.invalidateQueries({ queryKey: ['apartments'] });
    },
  });
};

export const useRejectInvitation = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ apartmentId, token }: { apartmentId: string; token: string }) =>
      coOwnershipInvitationService.rejectInvitation(apartmentId, token),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: INVITATION_KEYS.received() });
    },
  });
};

export const useRemoveCoOwner = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ apartmentId, ownerSub }: { apartmentId: string; ownerSub: string }) =>
      coOwnershipInvitationService.removeCoOwner(apartmentId, ownerSub),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['apartments'] });
    },
  });
};
